import { useState, useRef, useEffect } from "react";
import { ResultOf } from "gql.tada";
import { useAnimate, useMotionValue } from "motion/react";
import ArrowIcon from "@hawkins/assets/lib/icons/iconArrowUpRight";
import { useIsDesktop } from "@hooks/useIsDesktop";
import { animationCurves } from "@utils/animationCurves";
import { ListItemFragment } from "@queries/fragments/ListItemFragment";
import { LinkFragment } from "@queries/fragments/LinkFragment";
import { Button } from "@components/shared/core/Button";
import { Link } from "@components/shared/core/Link";
import { RevealAnimationVariants } from "@utils/RevealAnimationVariants";
import { MarkdownLite } from "@components/shared/MarkdownLite";
import { Analytics } from "@utils/Analytics";

type TListItem = ResultOf<typeof ListItemFragment>;

type ListItemProps = {
  item: TListItem;
  index: number;
  height: number;
  centeredItem: HTMLDivElement | null;
  mobileVpObserver?: IntersectionObserver;
  onMouseOver: (idx: number) => void;
  locale: string;
};

const ListItem = ({
  item,
  index,
  height,
  centeredItem,
  mobileVpObserver,
  onMouseOver,
  locale,
}: ListItemProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const elementRef = useRef<HTMLDivElement>(null);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (!isDesktop) {
      const element = elementRef.current;
      if (element && mobileVpObserver) {
        mobileVpObserver.observe(element);
      }
      return () => {
        if (element && mobileVpObserver) {
          mobileVpObserver.unobserve(element);
        }
      };
    }
  }, [mobileVpObserver, isDesktop]);

  useEffect(() => {
    if (!isDesktop) {
      if (elementRef.current?.isEqualNode(centeredItem)) {
        setIsPlaying(true);
        if (item?.video?.url) {
          videoRef.current?.play();
        }
      } else {
        setIsPlaying(false);
        if (item?.video?.url && videoRef.current) {
          videoRef.current.pause();
          videoRef.current.currentTime = 0;
        }
      }
    }
  }, [centeredItem, isDesktop, item?.video?.url]);

  const handleMouseOver = () => {
    if (!isDesktop) return;
    setIsPlaying(true);
    if (item?.video?.url) {
      videoRef.current?.play();
    }
    onMouseOver(index);
  };

  const handleMouseOut = () => {
    if (!isDesktop) return;
    if (item?.video?.url && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setIsPlaying(false);
  };

  const handleLinkClick = () => {
    Analytics.trackEvent(`still_watching_${locale}_report_cta_click`);
  };

  const inner = (
    <div
      ref={elementRef}
      onMouseEnter={handleMouseOver}
      onMouseLeave={handleMouseOut}
      className="stillWatching-item w-full flex flex-col dsk:flex-row dsk:justify-between py-8 px-4 dsk:px-36 z-10"
      style={{ height: isDesktop ? height : "inherit" }}
    >
      {isDesktop ? (
        <DesktopContent item={item} isPlaying={isPlaying} />
      ) : (
        <MobileContent item={item} />
      )}

      <div className="w-full dsk:w-[300px] flex flex-col justify-center">
        {!!item?.videoPosterImage?.url && (
          <div className="relative rounded-lg aspect-[4/3] h-[206px]">
            <Poster item={item} isPlaying={isPlaying} isDesktop={isDesktop} />
            {!!item?.video?.url && (
              <video
                ref={videoRef}
                className="absolute top-0 left-0 h-full object-cover rounded-lg transition duration-long"
                style={{
                  opacity: isPlaying ? 1 : 0,
                  transform: `scale(${isPlaying && isDesktop ? 1.1 : 1})`,
                }}
                src={item.video.url}
                playsInline
                loop
                muted
              />
            )}
          </div>
        )}
      </div>

      {!isDesktop && (
        <Button className="mt-5" asChild>
          <div className="flex items-center gap-2">
            <span>{item.ctaText}</span>
            <ArrowIcon size="small" />
          </div>
        </Button>
      )}
    </div>
  );

  if (item.externalUrl) {
    return (
      <Link href={item.externalUrl} onClick={handleLinkClick}>
        {inner}
      </Link>
    );
  }

  return (
    <Link link={item.ctaLink as unknown as ResultOf<typeof LinkFragment>}>
      {inner}
    </Link>
  );
};

const DesktopContent = ({
  item,
  isPlaying,
}: {
  item: TListItem;
  isPlaying: boolean;
}) => {
  const [scope, animate] = useAnimate();
  const titleY = useMotionValue(0);

  useEffect(() => {
    animate(titleY, isPlaying ? -74 : 0, animationCurves.macro);
  }, [isPlaying, animate, titleY]);

  return (
    <div className="flex flex-col gap-10 justify-center z-10">
      <p className="text-brand-cream">
        <MarkdownLite>{item?.eyebrow}</MarkdownLite>
      </p>
      <div ref={scope} className="flex flex-col gap-2 relative transition">
        <h3 className="text-heading-3 text-current">{item.title}</h3>
        <h3
          className="text-heading-3 text-brand-cream absolute inset-0 select-none"
          aria-hidden
          style={{
            transitionTimingFunction: `cubic-bezier(${animationCurves.macro.ease.join(", ")})`,
            transitionProperty: RevealAnimationVariants.clipPath.cssProperty,
            transitionDuration: `${animationCurves.macro.duration}s`,
            [RevealAnimationVariants.clipPath.property]:
              RevealAnimationVariants.clipPath.variant[isPlaying ? "visible" : "hidden"],
          }}
        >
          {item.title}
        </h3>
        {!!item.subtitle && (
          <h5 className={`transition duration-long ${isPlaying ? "text-brand-cream" : "text-current"}`}>
            {item.subtitle}
          </h5>
        )}
      </div>
      <div
        className="text-brand-cream text-button-small transition duration-long flex gap-1 items-center"
        style={{ opacity: isPlaying ? 1 : 0 }}
      >
        {item.ctaText}
        <ArrowIcon size="small" />
      </div>
    </div>
  );
};

const MobileContent = ({ item }: { item: TListItem }) => (
  <div>
    <span className="text-brand-cream text-body-regular">{item?.eyebrow}</span>
    <h3 className="text-heading-3 mb-8">{item.title}</h3>
    {!!item.subtitle && <h5 className="text-current mb-8">{item.subtitle}</h5>}
  </div>
);

const Poster = ({
  item,
  isPlaying,
  isDesktop,
}: {
  item: TListItem;
  isPlaying: boolean;
  isDesktop: boolean;
}) => {
  const hasVideo = !!item?.video?.url;

  return (
    <div
      className="h-full transition duration-long"
      style={{
        opacity: hasVideo ? (isPlaying ? 0 : 1) : 1,
        transform: !hasVideo && isPlaying && isDesktop ? "scale(1.1)" : "scale(1)",
      }}
    >
      <div
        className="absolute w-full h-full top-0 left-0 bg-brand-black rounded-lg z-20 transition duration-long"
        style={{ opacity: isPlaying ? 0 : 0.4 }}
      />
      {!!item?.logo?.url && (
        <div className="flex justify-center items-center h-full">
          <img src={item.logo.url} className="z-20 w-36" />
        </div>
      )}
      {!!item?.videoPosterImage?.url && (
        <img
          className="w-full h-full rounded-lg absolute top-0 left-0 z-10 object-cover"
          src={item.videoPosterImage.url}
        />
      )}
    </div>
  );
};

export default ListItem;
